import React from 'react';
import { motion } from 'framer-motion';
import { BrainIcon, TargetIcon, WrenchIcon, XIcon, CheckIcon } from 'lucide-react';
export function Approach() {
  const pillars = [{
    title: 'The Whole Person',
    description: 'I look beyond just the injury to understand how your sleep, stress, lifestyle habits, and mindset all play a role in your recovery. This bigger-picture approach helps me find the real drivers of your pain — not just the symptoms.',
    icon: <BrainIcon size={28} className="text-amber-500" data-id="element-46" data-mp="1e1e0raq0ct60" />
  }, {
    title: 'Goal-Driven Programming',
    description: 'For athletes, I program with the end goal in mind — making sure your rehab progressively exposes you to the demands of your sport or activity. Every exercise has a purpose tied to getting you back to what you love, stronger than before.',
    icon: <TargetIcon size={28} className="text-amber-500" data-id="element-47" data-mp="3ot5o8rdk221v" />
  }, {
    title: 'Practical & Individualized',
    description: "No cookie-cutter protocols. I build plans around the resources you actually have — your gym, your schedule, your equipment. The result is a program that fits your life and is something you'll actually follow through on.",
    icon: <WrenchIcon size={28} className="text-amber-500" data-id="element-48" data-mp="2gvnytt88yehz" />
  }];
  const traditionalItems = ['2–3 visits per week, 15–20 minutes with your PT', 'Passed off to an aide for most of your session', 'Mostly passive treatments: ice, heat, ultrasound, e-stim, massage', 'Cookie-cutter exercise plan with no real progression', 'Zero communication between sessions', 'A cycle of no progress, dropout, and returning to the same pain'];
  const mcleanItems = ["Focused 1-on-1 sessions to understand what's driving your symptoms", 'Comprehensive evaluation, objective testing, and hands-on coaching to guide your progress', 'Exercise-based care with intentional progression', 'Custom programming delivered straight to your phone', 'Messaging support and form review between visits', 'A clear path from pain to performance—and long-term independence'];
  return <section id="approach" className="py-24 bg-charcoal-950 text-offwhite-50" data-id="element-49" data-mp="158x0rau2qypu">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-50" data-mp="10cew0ayqv0di">
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-24" data-id="element-51" data-mp="vdc3fpzyammf">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-52" data-mp="vazcinc7t1x8">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-53" data-mp="1e2a12xugx2ly">My Approach</h2>
            <p className="text-xl text-offwhite-200 mb-6 leading-relaxed" data-id="element-54" data-mp="7nr7m6ip7uvq">
              Every plan starts with the end in mind. I build each program
              around your goals, your resources, and your preferences—guided by
              evidence-based principles.
            </p>
            <p className="text-lg text-offwhite-200 leading-relaxed" data-id="element-55" data-mp="2jabjmxqynnot">
              By combining in-person sessions for assessment, coaching, and
              progression with custom remote programming delivered to your
              phone, you get an individualized plan that works on the days we
              see each other — and every day in between.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="bg-charcoal-900 p-8 md:p-10 rounded-3xl border border-charcoal-800 relative overflow-hidden" data-id="element-56" data-mp="3212n5io9ojva">
            <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl -mr-20 -mt-20" data-id="element-57" data-mp="1hlr7qv09hpdk"></div>
            <h3 className="text-2xl font-bold mb-8 relative z-10" data-id="element-58" data-mp="384fn8hmziorj">
              How It Works
            </h3>
            <div className="space-y-8 relative z-10" data-id="element-59" data-mp="3h3150akmtzr9">
              <div className="flex gap-4" data-id="element-60" data-mp="3h1q3yiikn63u">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-500/20 text-amber-500 flex items-center justify-center font-bold" data-id="element-61" data-mp="3d1fx73mygbnx">
                  1
                </div>
                <div data-id="element-62" data-mp="2p9qv4vkc08ts">
                  <h4 className="text-lg font-bold mb-1" data-id="element-63" data-mp="2kd0iki7qjvd4">
                    20-Minute Discovery Call
                  </h4>
                  <p className="text-offwhite-200 text-sm" data-id="element-64" data-mp="2y56zw7mmk8e5">
                    We'll talk through what's been going on, what you've tried,
                    your goals, and whether this model is the right fit for you.
                  </p>
                </div>
              </div>
              <div className="flex gap-4" data-id="element-65" data-mp="21msmul6d876y">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-500/20 text-amber-500 flex items-center justify-center font-bold" data-id="element-66" data-mp="21lr6q2ahsuid">
                  2
                </div>
                <div data-id="element-67" data-mp="1w9u7keqzyonl">
                  <h4 className="text-lg font-bold mb-1" data-id="element-68" data-mp="1wb5aydgj5ue6">
                    In-Person or Virtual Evaluation
                  </h4>
                  <p className="text-offwhite-200 text-sm" data-id="element-69" data-mp="1mizjr5y3rh01">
                    We'll take a deeper look at your history, current
                    limitations, and goals so I can understand the full picture
                    and recommend the right plan.
                  </p>
                </div>
              </div>
              <div className="flex gap-4" data-id="element-70" data-mp="20bfj9wrsoww7">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-500/20 text-amber-500 flex items-center justify-center font-bold" data-id="element-71" data-mp="20cqkppxrpsk5">
                  3
                </div>
                <div data-id="element-72" data-mp="ou5xh3l71srq">
                  <h4 className="text-lg font-bold mb-1" data-id="element-73" data-mp="oxtr02qn2f6g">
                    Custom App Programming
                  </h4>
                  <p className="text-offwhite-200 text-sm" data-id="element-74" data-mp="2whqvzltnsghr">
                    Your rehab and training plan is delivered straight to your
                    phone, with clear exercises, progressions, and communication
                    between visits.
                  </p>
                </div>
              </div>
              <div className="flex gap-4" data-id="element-75" data-mp="2ht4epekh1enw">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-500/20 text-amber-500 flex items-center justify-center font-bold" data-id="element-76" data-mp="2hq8ibuo9uy1k">
                  4
                </div>
                <div data-id="element-77" data-mp="28qd2hcjruev7">
                  <h4 className="text-lg font-bold mb-1" data-id="element-78" data-mp="28u0xkg4v79c1">In-Person Visits</h4>
                  <p className="text-offwhite-200 text-sm" data-id="element-79" data-mp="23stp0euwe64b">
                    We meet based on the level of support required to reach your
                    goals. Follow-up visits are used to assess progress, make
                    key adjustments, and coach you through what your independent
                    training sessions should feel like.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="mb-24" data-id="element-80" data-mp="9joa8ra9o44i">
          <h3 className="text-3xl md:text-4xl font-bold text-center mb-4" data-id="element-81" data-mp="3hu9edem7ayot">
            Not All PT Is Created Equal
          </h3>
          <p className="text-offwhite-200 text-center text-lg mb-12 max-w-2xl mx-auto" data-id="element-82" data-mp="3rqm6w960f6hz">
            If your rehab feels like a revolving door, it's not you — it's the
            model.
          </p>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-83" data-mp="170myjyl1yveu">
            {/* Traditional */}
            <div className="bg-charcoal-900 rounded-2xl border border-charcoal-800 p-8 md:p-10" data-id="element-84" data-mp="1bz6p6calcvdj">
              <div className="flex items-center gap-3 mb-8" data-id="element-85" data-mp="1x38t0nafpule">
                <div className="w-10 h-10 rounded-full bg-red-500/15 flex items-center justify-center" data-id="element-86" data-mp="1x3iim9rkhzqk">
                  <XIcon size={20} className="text-red-400" data-id="element-87" data-mp="1s5z4rays7oz5" />
                </div>
                <h4 className="text-xl font-bold text-offwhite-100" data-id="element-88" data-mp="26umx78dbzikv">
                  Traditional Rehab
                </h4>
              </div>
              <ul className="space-y-5" data-id="element-89" data-mp="2ftqgacchzxzd">
                {traditionalItems.map((item, i) => <motion.li key={i} initial={{
                opacity: 0,
                x: -10
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.4,
                delay: i * 0.1
              }} className="flex items-start gap-3" data-id="element-90" data-mp="2bs57b23343ug" data-mpi={i}>
                    <XIcon size={18} className="text-red-400/60 flex-shrink-0 mt-1" data-id="element-91" data-mp="324i4w161hn6q" />
                    <span className="text-offwhite-200/70" data-id="element-92" data-mp="2sbaxkarqnx40">{item}</span>
                  </motion.li>)}
              </ul>
            </div>

            {/* McLean PT */}
            <div className="bg-charcoal-900 rounded-2xl border border-amber-500/30 p-8 md:p-10 relative overflow-hidden" data-id="element-93" data-mp="2jbeh2wfi64k1">
              <div className="absolute top-0 right-0 w-48 h-48 bg-amber-500/5 rounded-full blur-3xl -mr-12 -mt-12" data-id="element-94" data-mp="2obirq5tqc52c"></div>
              <div className="flex items-center gap-3 mb-8 relative z-10" data-id="element-95" data-mp="29fkx4rzgf8s8">
                <div className="w-10 h-10 rounded-full bg-amber-500/15 flex items-center justify-center" data-id="element-96" data-mp="2edcp1m03muwf">
                  <CheckIcon size={20} className="text-amber-500" data-id="element-97" data-mp="7mmxodb69hyb" />
                </div>
                <h4 className="text-xl font-bold text-offwhite-50" data-id="element-98" data-mp="cjwysq7xbi1z">
                  McLean PT
                </h4>
              </div>
              <ul className="space-y-5 relative z-10" data-id="element-99" data-mp="2lu8e0ntiglyd">
                {mcleanItems.map((item, i) => <motion.li key={i} initial={{
                opacity: 0,
                x: -10
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.4,
                delay: i * 0.1
              }} className="flex items-start gap-3" data-id="element-100" data-mp="2qoma6sfzyx43" data-mpi={i}>
                    <CheckIcon size={18} className="text-amber-500 flex-shrink-0 mt-1" data-id="element-101" data-mp="2zpaqxuwrzbko" />
                    <span className="text-offwhite-100" data-id="element-102" data-mp="1o9dod3w0kt4z">{item}</span>
                  </motion.li>)}
              </ul>
            </div>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-10" data-id="element-103" data-mp="1ce6w9yz0q1f">
          {pillars.map((pillar, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: index * 0.2
        }} className="bg-charcoal-900/50 p-8 rounded-2xl border border-charcoal-800" data-id="element-104" data-mp="fbnqljmdy2a5" data-mpi={index}>
              <div className="mb-6" data-id="element-105" data-mp="3npmjgce297jf">{pillar.icon}</div>
              <h3 className="text-xl font-bold mb-3" data-id="element-106" data-mp="301d3hr3j3e07">{pillar.title}</h3>
              <p className="text-offwhite-200 leading-relaxed" data-id="element-107" data-mp="2zz8oad06iskm">
                {pillar.description}
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
}